import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CrudAPI,
  CrudService,
  TranslateGenericApiValidationError,
} from '@core/api';

// Known leak, ratcheted rather than excused: this value import puts
// DevExtreme's runtime in the NativeScript bundle, measured at 1.53 MiB (9.3%
// of vendor.js) in docs/analysis/mobile-bundle-devextreme-leak.md. The rule errors on
// any NEW devextreme import here; removing these eight disables is what fixing
// that leak means.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import notify from 'devextreme/ui/notify';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  of,
  share,
  Subject,
  switchMap,
} from 'rxjs';
import { Role, TimeLog, User, UserForm } from '../models';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userApi!: CrudAPI<User>;
  public userCrudApi!: CrudAPI<User>;
  public roleCrudApi!: CrudAPI<Role>;
  private userSearch = new Subject<any[]>();

  private users = new BehaviorSubject<User[]>([]);
  public users$ = this.users.asObservable();

  public user = new BehaviorSubject<User>({} as User);
  public user$ = this.user.asObservable();

  public roles = new BehaviorSubject<Role[]>([]);
  public roles$ = this.roles.asObservable();

  public logs = new BehaviorSubject<TimeLog[]>([]);
  public logs$ = this.logs.asObservable();

  public readonly searchResult$ = this.userSearch.pipe(
    distinctUntilChanged(),
    filter((x) => x[0].length >= 3),
    switchMap(([query, projectId]) =>
      this.crud.readMany(
        `api/component/users/usersAutoComplete?search=${query}&projectId=${projectId}`
      )
    ),
    map(({ data }: any) => data), // transform data if necessary
    share()
  );

  constructor(
    private crud: CrudService,
    private translateService: TranslateService,
    private translateGenericApiValidationError: TranslateGenericApiValidationError
  ) {
    this.userApi = crud.forEndpoint('api/component/users');
    this.userCrudApi = crud.forEndpoint('api/crud/users');
    this.roleCrudApi = crud.forEndpoint('api/crud/roles');
  }

  public searchWithoutProject(input: string, projectId: number | null) {
    this.userSearch.next([input, projectId]);
  }

  public getUser(id: number) {
    this.userCrudApi
      .readOne(id, `with=userDetails%3Bprojects%3Broles`)
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of({} as User);
        })
      )
      .subscribe((x) => {
        if (x) {
          this.user.next(x);
        }
      });
  }

  public loadUsers() {
    this.userCrudApi
      .readMany(`with=userDetails%3Broles`)
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as User[]);
        })
      )
      .subscribe((x) => this.users.next(x));
  }

  public updateUser(id: number | null, data: Partial<UserForm>) {
    this.crud
      .forEndpoint('api/component/users/update')
      .createOne({
        ...data,
        id,
        projects:
          data.projects instanceof Array ? data.projects.map((x) => x.id) : [],
      })
      .subscribe({
        next: () => this.getUser(id as number),
        error: (e) => notify(e.message, 'error'),
      });
  }

  public createUser(data: UserForm) {
    return this.crud
      .forEndpoint('api/component/users/create')
      .createOne(data)
      .pipe(
        catchError((e) => {
          if (Object.prototype.hasOwnProperty.call(e, 'error')) {
            const errors = this.translateGenericApiValidationError.translate(e);
            notify(errors, 'error', 10000);
          }
          return e;
        })
      );
  }

  sendOnboardingMail(data: User) {
    this.userApi
      .readOne('sendOnboardingMailToUser?email=' + data.email)
      .pipe(map((x: any) => x))
      .subscribe({
        next: (result) => {
          notify(result.message || 'Success', 'success');
        },
        error: (error) => {
          notify(error.message, 'error');
        },
      });
    console.log('Sending Onboarding Mail for ID: ', data.email);
  }

  getTimeLog(userId: number | null) {
    this.crud
      .forEndpoint('api/reports/analyseInventoryItems?user_id=' + userId)
      .readMany()
      .pipe()
      .subscribe((x) => this.logs.next(x));
  }
  getRoles() {
    this.crud
      .forEndpoint('api/component/users/roles')
      .readMany()
      .pipe(
        map(({ data }: any) => data as Role[]),
        mergeMap((x) =>
          this.translateService
            .get(x.map((y) => 'person.roles.' + y.name))
            .pipe(
              map((labels) =>
                x.map((z) => ({
                  ...z,
                  label: labels['person.roles.' + z.name],
                }))
              )
            )
        ),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as Role[]);
        })
      )
      .subscribe((x) => this.roles.next(x));
  }
}
